import { NetworkStatus } from '@apollo/client'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import { EmployeeSearch, KudosFilterInput } from '../../types'
import { KudosDashboardView, KudosTable } from './components/KudosTable'
import { useAllKudos } from '../../graphql/kudos/useKudosQueries'
import PerSpacer from '../../components/custom_components/Spacer'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import KudosHRInfoBox from './components/KudosHRInfoBox'
import { ShowMoreButton } from '../reports/table/ShowMoreButton'
import { KUDOS_DASHBOARD_PAGE_SIZE } from '../../graphql/kudos/util'

export const KudosHRDashboard: FC<{
  filters: KudosFilterInput
  setFilters: (filters: KudosFilterInput) => void
  selectedEmployee: EmployeeSearch | null
  selectedAuthor: EmployeeSearch | null
  setSelectedEmployee: (employee: EmployeeSearch | null) => void
  setSelectedAuthor: (author: EmployeeSearch | null) => void
}> = ({ filters, setFilters, selectedEmployee, selectedAuthor, setSelectedEmployee, setSelectedAuthor }) => {
  const { t } = useTranslation()

  const [firstLoadingDone, setFirstLoadingDone] = useState(false)

  const { data, loading, error, fetchMore, networkStatus } = useAllKudos(filters)

  useEffect(() => {
    if (!!data?.getAllKudos) {
      setFirstLoadingDone(true)
    }
  }, [data])

  const handleFiltersChange = (newFilters: KudosFilterInput) => {
    setFilters(newFilters)
  }

  const loadMore = () => {
    if (data && data.getAllKudos.hasMore) {
      fetchMore({
        variables: {
          filters: {
            ...filters,
            pagination: {
              offset: data.getAllKudos.nextOffset || 0,
              limit: KUDOS_DASHBOARD_PAGE_SIZE,
            },
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult
          }

          const previousKudos = previousResult.getAllKudos.kudos
          const fetchMoreKudos = fetchMoreResult.getAllKudos.kudos

          fetchMoreResult.getAllKudos.kudos = [...previousKudos, ...fetchMoreKudos]

          return { ...fetchMoreResult }
        },
      })
    }
  }

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore
  const showMoreLayoutActive = !!data?.getAllKudos?.hasMore && (!loading || isLoadingMore)

  if ((error || loading) && !firstLoadingDone) {
    return (
      <BackgroundBox r height="withTabs">
        <NewLoadingAndErrors isLoading={loading} error={error} hasNoData={false} showSpinner t={t} />
      </BackgroundBox>
    )
  }

  return (
    <>
      <KudosHRInfoBox
        filters={filters}
        onFiltersChange={handleFiltersChange}
        selectedAuthor={selectedAuthor}
        setSelectedAuthor={setSelectedAuthor}
        selectedEmployee={selectedEmployee}
        setSelectedEmployee={setSelectedEmployee}
      />
      <PerSpacer mb="xs" />
      <KudosTable
        kudos={data?.getAllKudos?.kudos}
        loading={loading}
        isLoadingMore={isLoadingMore}
        error={error}
        filters={filters}
        showMoreButtonVisible={showMoreLayoutActive}
        kudosDashboardView={KudosDashboardView.hr}
      />

      <ShowMoreButton visible={showMoreLayoutActive} loadingMore={isLoadingMore} onLoadMore={loadMore} />
    </>
  )
}
